/**
 * Utility functions for contract routing based on status and user role
 */

export type UserRole = 'buyer' | 'seller' | 'agent';

export interface ContractRoutingInfo {
  contractId: string;
  contractStatus?: string;
  userRole: UserRole;
}

/**
 * Determines the correct route for viewing a contract based on status and user role
 * @param contractInfo - Contract information including ID, status, and user role
 * @returns The appropriate route path
 */
export const getContractViewRoute = (
  contractInfo: ContractRoutingInfo
): string => {
  const { contractId, contractStatus, userRole } = contractInfo;

  // If contract is ratified, route to appropriate dashboard
  if (contractStatus === 'ratified') {
    switch (userRole) {
      case 'buyer':
        return `/buyer/property-dashboard/${contractId}`;
      case 'seller':
        return `/seller/property-dashboard/${contractId}`;
      case 'agent':
        return `/agent/property-dashboard/${contractId}`;
      default:
        return `/view-contract/${contractId}`;
    }
  }

  // For non-ratified contracts, route based on user role
  switch (userRole) {
    case 'buyer':
      return `/contract/${contractId}`;
    case 'seller':
      return `/view-contract/${contractId}`; // Same as Add Details page
    case 'agent':
      return `/agent/contract/${contractId}`;
    default:
      return `/view-contract/${contractId}`;
  }
};

/**
 * Determines the correct route for contract negotiation based on user role
 * @param contractId - Contract ID
 * @param userRole - User role
 * @returns The appropriate negotiation route path
 */
export const getContractNegotiationRoute = (
  contractId: string,
  userRole: UserRole
): string => {
  switch (userRole) {
    case 'buyer':
      return `/buyer/negotiate/${contractId}`;
    case 'seller':
      return `/seller/negotiate/${contractId}`;
    case 'agent':
      return `/agent/contract/${contractId}`;
    default:
      return `/view-contract/${contractId}`;
  }
};

/**
 * Determines the correct route for adding contract details based on user role
 * @param contractId - Contract ID
 * @param userRole - User role
 * @returns The appropriate add details route path
 */
export const getContractAddDetailsRoute = (
  contractId: string,
  userRole: UserRole
): string => {
  switch (userRole) {
    case 'buyer':
      return `/contract/${contractId}`;
    case 'seller':
      return `/seller/add-detail/${contractId}`;
    case 'agent':
      return `/contract/agent/${contractId}`;
    default:
      return `/view-contract/${contractId}`;
  }
};

/**
 * Gets the user role from the current route or context
 * This is a helper function that can be expanded based on your authentication system
 */
export const getCurrentUserRole = (): UserRole => {
  // This should be implemented based on your authentication system
  // For now, we'll try to infer from the current path
  const path = window.location.pathname;

  if (path.includes('/buyer/')) return 'buyer';
  if (path.includes('/seller/')) return 'seller';
  if (path.includes('/agent/')) return 'agent';

  // Default fallback - you might want to get this from a context or auth state
  return 'buyer';
};
